import React, { useEffect, useState } from "react";
import DigitalWalletCalendarIcon from "../../icons/DigitalWalletCalendarIcon";
import { translateLabel, useTranslationSnapshot } from "./../../translations/runtime";


const emptyRange = { from: null, to: null };
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const iso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const parseIso = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);

  return new Date(year, month - 1, day);
};

const displayDate = (value) =>
  value ? value.split("-").reverse().join("/") : "DD/MM/YYYY";

function DateRangeFilter({
  visible,
  value = emptyRange,
  onApply,
  onClose,
}) {
  useTranslationSnapshot();
  const [draft, setDraft] = useState(value);
  const [field, setField] = useState("from");
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    if (!visible) return;

    setDraft({ ...value });
    setField("from");
    setMonth(value.from ? parseIso(value.from) : new Date());
  }, [value, visible]);

  if (!visible) return null;

  const start = new Date(month.getFullYear(), month.getMonth(), 1);
  const days = Array.from(
    { length: 42 },
    (_, index) =>
      new Date(start.getFullYear(), start.getMonth(), index - start.getDay() + 1),
  );

  const chooseDay = (day) => {
    if (field === "from") {
      setDraft({
        from: day,
        to: draft.to && draft.to >= day ? draft.to : null,
      });
      setField("to");
      return;
    }

    if (draft.from && day < draft.from) {
      setDraft({ from: day, to: draft.from });
      return;
    }

    setDraft({ from: draft.from, to: day });
  };

  const clear = () => {
    setDraft({ ...emptyRange });
    setField("from");
  };

  return (
    <div className="wallet-date-range-backdrop" onMouseDown={onClose}>
      <div
        className="wallet-date-range-panel"
        role="dialog"
        aria-modal="true"
        aria-label={translateLabel("txt_admin_date_range_filter", "Date range filter")}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="wallet-date-range-heading">
          <div>
            <div className="wallet-date-range-title">{translateLabel("txt_web_vendor_filter", "Filter")}</div>
            <div className="wallet-date-range-subtitle">{translateLabel("input_date_range", "Date Range")}</div>
          </div>
          <button type="button" onClick={onClose} aria-label={translateLabel("txt_admin_close_date_range", "Close date range")}>
            &times;
          </button>
        </div>

        <div className="wallet-date-range-fields">
          {["from", "to"].map((key) => (
            <div className="wallet-date-range-field" key={key}>
              <label>{key === "from" ? translateLabel("txt_admin_from", "From") : translateLabel("txt_time_to", "To")}</label>
              <button
                type="button"
                className={field === key ? "active" : ""}
                onClick={() => {
                  setField(key);
                  if (draft[key]) setMonth(parseIso(draft[key]));
                }}
              >
                <span>{displayDate(draft[key])}</span>
                <DigitalWalletCalendarIcon />
              </button>
            </div>
          ))}
        </div>

        <div className="wallet-date-range-calendar">
          <div className="wallet-date-range-month-row">
            <button
              type="button"
              onClick={() =>
                setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
              }
              aria-label={translateLabel("txt_admin_previous_month", "Previous month")}
            ><span className="navigation-direction-icon navigation-direction-glyph">‹</span></button>
            <span>
              {months[month.getMonth()]} {month.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() =>
                setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
              }
              aria-label={translateLabel("txt_admin_next_month", "Next month")}
            ><span className="navigation-direction-icon navigation-direction-glyph">›</span></button>
          </div>

          <div className="wallet-date-range-grid">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div className="wallet-date-range-weekday" key={day}>
                {day}
              </div>
            ))}
            {days.map((day) => {
              const key = iso(day);
              const outside = day.getMonth() !== month.getMonth();
              const endpoint = key === draft.from || key === draft.to;
              const inside =
                draft.from && draft.to && key >= draft.from && key <= draft.to;

              return (
                <button
                  type="button"
                  className={[
                    "wallet-date-range-day",
                    outside ? "outside" : "",
                    inside ? "in-range" : "",
                    endpoint ? "endpoint" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={outside}
                  key={key}
                  onClick={() => chooseDay(key)}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        <div className="wallet-date-range-actions">
          <button type="button" className="secondary" onClick={clear}> {translateLabel("txt_admin_clear_all", "Clear All")} </button>
          <button
            type="button"
            className="primary"
            onClick={() => {
              onApply({ ...draft });
              onClose();
            }}
          > {translateLabel("txt_admin_apply_all", "Apply All")} </button>
        </div>
      </div>
    </div>
  );
}

export default DateRangeFilter;
